import { useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SEOHead from "@/components/feature/SEOHead";
import ScrollToTop from "@/components/feature/ScrollToTop";
import { categories, allContent } from "@/mocks/content";

const sortOptions = [
  { value: "latest", label: "最新更新" },
  { value: "rating", label: "评分最高" },
  { value: "popular", label: "最受欢迎" },
];

const categoryMeta: Record<string, { title: string; description: string; keywords: string }> = {
  drama: {
    title: "日韩剧集 - aaa日韩高清在线观看 | 正版日韩电视剧",
    description: "aaa日韩精选日韩最新电视剧集，涵盖爱情、悬疑、职场、古装等多种类型，高清画质流畅播放，每日更新最新日韩剧集资源，正版授权放心观看。",
    keywords: "日韩剧集,日韩电视剧,日剧在线,韩剧高清,日韩正版剧集,aaa日韩剧集",
  },
  movie: {
    title: "日韩电影 - aaa日韩高清电影在线 | 正版日韩影片",
    description: "aaa日韩为您提供最新日韩电影在线观看，院线热映与经典佳片一网打尽，4K超高清画质，原声配音中文字幕，正版日韩电影观看平台。",
    keywords: "日韩电影,日本电影,韩国电影,日韩高清电影,正版日韩影片,aaa日韩电影",
  },
  anime: {
    title: "动漫番剧 - aaa日韩动漫在线观看 | 正版日韩动漫",
    description: "aaa日韩动漫频道汇聚最新最全日韩动漫番剧，热门新番同步更新，经典动漫高清重温，二次元世界无限精彩尽在aaa日韩。",
    keywords: "动漫番剧,日韩动漫,日本动漫,新番在线,正版动漫,aaa日韩动漫",
  },
  variety: {
    title: "综艺娱乐 - aaa日韩综艺在线观看 | 正版日韩综艺",
    description: "aaa日韩综艺频道提供最新日韩热门综艺节目在线观看，Running Man、无限挑战等王牌综艺高清完整版，欢乐不断笑料百出。",
    keywords: "综艺娱乐,日韩综艺,韩国综艺,日本综艺,正版综艺,aaa日韩综艺",
  },
  music: {
    title: "音乐现场 - aaa日韩音乐高清 | 演唱会在线观看",
    description: "aaa日韩音乐频道汇集日韩音乐盛典、演唱会、MV高清合集，BTS、BLACKPINK等顶级团体演出实况，沉浸式音乐体验。",
    keywords: "音乐现场,日韩音乐,演唱会,MV高清,K-pop,aaa日韩音乐",
  },
  documentary: {
    title: "纪录片 - aaa日韩纪录片在线 | 日韩文化探索",
    description: "aaa日韩纪录片频道深度探索日韩文化、历史、美食与自然风光，高清纪录片带你走进真实的日本与韩国，开拓视野增长见识。",
    keywords: "纪录片,日韩纪录片,日本文化,韩国美食,自然风光,aaa日韩纪录片",
  },
};

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSort, setActiveSort] = useState("latest");

  const category = categories.find((c) => c.slug === slug);
  const meta = categoryMeta[slug || ""] || {
    title: "内容分类 - aaa日韩正版平台",
    description: "aaa日韩内容分类页面，按类型探索日韩正版优质内容",
    keywords: "aaa日韩,内容分类,日韩内容",
  };

  const contentList = useMemo(() => {
    const filtered = allContent.filter((item) => item.category === slug);
    if (activeSort === "rating") {
      return [...filtered].sort((a, b) => b.rating - a.rating);
    }
    if (activeSort === "latest") {
      return [...filtered].sort((a, b) => b.updateDate.localeCompare(a.updateDate));
    }
    return filtered;
  }, [slug, activeSort]);

  const categorySchema = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: meta.title,
      description: meta.description,
      url: `https://www.aaark.com/category/${slug}`,
      isPartOf: {
        "@type": "WebSite",
        name: "aaa日韩",
        url: "https://www.aaark.com",
      },
    },
  ];

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen pt-20 flex items-center justify-center">
          <p className="text-foreground-600">分类不存在</p>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        canonicalUrl={`https://www.aaark.com/category/${slug}`}
        schemaJson={categorySchema}
      />
      <ScrollToTop />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: category.name },
            ]}
          />

          {/* Category Header */}
          <section className="px-4 md:px-6 py-8 md:py-12">
            <div className="flex items-center gap-4 mb-4">
              <div className={`w-12 h-12 flex items-center justify-center rounded-full bg-gradient-to-br ${category.color}`}>
                <i className={`${category.icon} text-xl text-white`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-600 mt-1">{category.description}</p>
              </div>
            </div>
          </section>

          {/* Category Tabs + Sort */}
          <section className="px-4 md:px-6 pb-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-2 flex-wrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 text-sm rounded-full transition-all whitespace-nowrap cursor-pointer ${
                      cat.slug === slug
                        ? "bg-primary-500 text-background-50 font-medium"
                        : "bg-background-100 text-foreground-700 hover:bg-background-200/70"
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>

              <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setActiveSort(opt.value)}
                    className={`px-3 py-1.5 text-xs rounded-full transition-all whitespace-nowrap cursor-pointer ${
                      activeSort === opt.value
                        ? "bg-background-50 text-foreground-950 font-medium shadow-sm"
                        : "text-foreground-600 hover:text-foreground-800"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Content Grid */}
          <section className="px-4 md:px-6 py-8">
            {contentList.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {contentList.map((item) => (
                  <Link
                    key={item.id}
                    to={`/article/${item.id}`}
                    className="group rounded-lg overflow-hidden bg-background-100 hover:bg-background-200/70 transition-all cursor-pointer"
                  >
                    <div className="relative aspect-video overflow-hidden">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-2 right-2 px-2 py-0.5 rounded text-xs font-medium bg-accent-500 text-background-50">
                        {item.rating}
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
                        {item.title}
                      </h3>
                      <div className="flex items-center gap-3 mt-2 text-xs text-foreground-500">
                        <span>{item.duration}</span>
                        <span>{item.episode}</span>
                      </div>
                      <p className="text-xs text-foreground-400 mt-2">{item.updateDate} 更新</p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-foreground-500">该分类暂无内容，敬请期待</p>
              </div>
            )}
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}